import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, LogOut, FolderOpen, Link2, Copy, Check, ExternalLink,
  MapPin, Phone, Mail, Building2, Wrench, Calendar, Euro, Download,
  FileText, Eye, Clock, CheckCircle, Network, TrendingUp, Users,
  Edit2, Save, X, Shield, EyeOff, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReseauDashboard() {
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chantiers');
  const [transferredProjects, setTransferredProjects] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [activatingAffiliation, setActivatingAffiliation] = useState(false);

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });

  useEffect(() => {
    const token = localStorage.getItem('network_token');
    const memberData = localStorage.getItem('network_member');
    
    if (!token || !memberData) {
      navigate('/reseau/login');
      return;
    }

    try {
      const parsed = JSON.parse(memberData);
      setMember(parsed);
      fetchData(token);
    } catch (e) {
      navigate('/reseau/login');
    }
  }, [navigate]);

  const fetchData = async (token) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Charger les données FRAÎCHES du membre depuis la base
      const meRes = await axios.get(`${API}/reseau/me`, { headers }).catch(() => null);
      if (meRes?.data) {
        setMember(meRes.data);
        localStorage.setItem('network_member', JSON.stringify(meRes.data));
        setProfileForm({
          nom: meRes.data.nom || '',
          prenom: meRes.data.prenom || '',
          telephone: meRes.data.telephone || meRes.data.phone || '',
          company_name: meRes.data.company_name || meRes.data.company_info?.name || '',
          siret: meRes.data.siret || meRes.data.company_info?.siret || '',
          address: meRes.data.address || meRes.data.company_info?.address || '',
          city: meRes.data.city || meRes.data.company_info?.city || '',
          postal_code: meRes.data.postal_code || meRes.data.company_info?.postal_code || '',
        });
      }

      const projectsRes = await axios.get(`${API}/reseau/my-projects`, { headers }).catch(() => ({ data: [] }));
      setTransferredProjects(projectsRes.data);

      const statsRes = await axios.get(`${API}/reseau/my-affiliate-stats`, { headers }).catch(() => ({ data: null }));
      setAffiliateStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('network_token');
    localStorage.removeItem('network_member');
    navigate('/reseau/login');
    toast.success('Déconnexion réussie');
  };

  const getAffiliateLink = () => {
    const code = member?.affiliate_code;
    if (!code) return null;
    return `${window.location.origin}/?ref=${code}`;
  };

  const copyAffiliateLink = () => {
    const link = getAffiliateLink();
    if (link) {
      navigator.clipboard.writeText(link);
      setCopiedLink(true);
      toast.success('Lien copié !');
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const activateAffiliation = async () => {
    const token = localStorage.getItem('network_token');
    setActivatingAffiliation(true);
    try {
      const res = await axios.post(`${API}/reseau/activate-affiliation`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Affiliation activée ! Votre lien a été envoyé par email.');
      // Recharger les données fraîches
      await fetchData(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActivatingAffiliation(false);
    }
  };

  const saveProfile = async () => {
    const token = localStorage.getItem('network_token');
    setSavingProfile(true);
    try {
      await axios.put(`${API}/reseau/profile`, profileForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Profil mis à jour !');
      await fetchData(token);
      setEditingProfile(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (!passwordForm.current_password) { toast.error('Entrez votre mot de passe actuel'); return; }
    if (passwordForm.new_password.length < 6) { toast.error('Minimum 6 caractères'); return; }
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('Les mots de passe ne correspondent pas'); return; }
    const token = localStorage.getItem('network_token');
    setSavingPassword(true);
    try {
      await axios.put(`${API}/reseau/password`, { current_password: passwordForm.current_password, new_password: passwordForm.new_password }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mot de passe modifié !');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingPassword(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '0,00 €';
    return new Intl.NumberFormat('fr-FR', { 
      style: 'currency', 
      currency: 'EUR', 
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  if (!member) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  const isArtisan = member.member_type === 'artisan';

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-navy text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/reseau/login')} 
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />Retour
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <div>
                <h1 className="font-heading text-xl font-bold">MON ESPACE RÉSEAU</h1>
                <p className="text-xs text-white/60">L'Œil du Marchand</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-medium">{member.full_name}</p>
                <p className="text-xs text-white/60">{member.company_info?.name || member.email}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout} 
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Welcome Card */}
        <Card className="mb-6 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-1">Bienvenue, {member.full_name} !</h2>
                <p className="text-white/80">
                  {member.member_type === 'artisan' && 'Artisan / Société'}
                  {member.member_type === 'affilie' && 'Affilié'}
                  {member.member_type === 'partenaire' && 'Partenaire'}
                  {member.member_type === 'chiffreur' && 'Chiffreur'}
                  {member.member_type === 'analyste' && 'Analyste'}
                </p>
              </div>
              <div className="flex gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold">{transferredProjects.length}</p>
                  <p className="text-sm text-white/80">Chantiers</p>
                </div>
                {affiliateStats && (
                  <div className="text-center">
                    <p className="text-3xl font-bold">{formatPrice(affiliateStats.total_commission || 0)}</p>
                    <p className="text-sm text-white/80">Commissions</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white shadow-sm mb-6 p-1">
            {isArtisan && (
              <TabsTrigger value="chantiers" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
                <FolderOpen className="w-4 h-4 mr-2" />
                Mes Chantiers ({transferredProjects.length})
              </TabsTrigger>
            )}
            <TabsTrigger value="affiliation" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white">
              <Link2 className="w-4 h-4 mr-2" />
              Affiliation
            </TabsTrigger>
            <TabsTrigger value="profil" className="data-[state=active]:bg-gray-700 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />
              Mon Profil
            </TabsTrigger>
          </TabsList>

          {/* Chantiers Tab - For Artisans */}
          {isArtisan && (
            <TabsContent value="chantiers">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-emerald-500" />
                    Mes Chantiers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
                    </div>
                  ) : transferredProjects.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <FolderOpen className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <h3 className="text-lg font-medium mb-2">Aucun chantier pour le moment</h3>
                      <p className="text-sm">Les dossiers transférés par l'administrateur apparaîtront ici.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {transferredProjects.map((project) => (
                        <div key={project.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  {project.property_type || 'Bien'}
                                </Badge>
                                <span className="text-sm text-gray-500">
                                  Reçu le {formatDate(project.transferred_at)}
                                </span>
                              </div>
                              <h4 className="font-semibold text-lg mb-1">{project.address}</h4>
                              <div className="flex items-center gap-4 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {project.city} {project.postal_code}
                                </span>
                                {project.surface && (
                                  <span>{project.surface} m²</span>
                                )}
                              </div>
                              
                              {/* Client info if available */}
                              {project.client_info && (
                                <div className="mt-3 p-3 bg-gray-50 rounded">
                                  <p className="text-sm font-medium mb-1">Contact Client:</p>
                                  <div className="flex items-center gap-4 text-sm text-gray-600">
                                    {project.client_info.name && (
                                      <span>{project.client_info.name}</span>
                                    )}
                                    {project.client_info.phone && (
                                      <span className="flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {project.client_info.phone}
                                      </span>
                                    )}
                                    {project.client_info.email && (
                                      <span className="flex items-center gap-1">
                                        <Mail className="w-3 h-3" />
                                        {project.client_info.email}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Notes */}
                              {project.transfer_notes && (
                                <div className="mt-2 p-2 bg-amber-50 rounded text-sm">
                                  <span className="font-medium">Notes: </span>
                                  {project.transfer_notes}
                                </div>
                              )}
                            </div>
                            
                            {/* Actions */}
                            <div className="flex flex-col gap-2 ml-4">
                              {project.pdf_url && (
                                <Button size="sm" variant="outline" asChild>
                                  <a href={project.pdf_url} target="_blank" rel="noopener noreferrer">
                                    <Download className="w-4 h-4 mr-1" />
                                    Rapport PDF
                                  </a>
                                </Button>
                              )}
                              <Button size="sm" variant="outline">
                                <Eye className="w-4 h-4 mr-1" />
                                Voir détails
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Affiliation Tab */}
          <TabsContent value="affiliation">
            <div className="space-y-6">

              {/* === BANNIÈRE INTRO === */}
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                <h3 className="font-bold text-lg text-amber-900 mb-1">Gagnez de l'argent en parlant de nous</h3>
                <p className="text-sm text-amber-800 mb-3">Vous avez des contacts, des clients, des voisins avec un projet immobilier ? Partagez votre lien — pour chaque audit payé, vous touchez automatiquement votre commission.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: 'Audit Flash', price: '97€', commission: '14,55€' },
                    { label: 'Audit Pro', price: '247€', commission: '37,05€' },
                    { label: 'Audit Master VIP', price: '497€', commission: '74,55€' },
                    { label: 'Partenaire (1ère année)', price: '500€', commission: '75€', highlight: true },
                  ].map((item) => (
                    <div key={item.label} className={`rounded-lg p-2 text-center border ${item.highlight ? 'bg-blue-50 border-blue-200' : 'bg-white border-amber-100'}`}>
                      <p className="text-xs text-gray-500 font-medium">{item.label}</p>
                      <p className="text-xs text-gray-400">{item.price}</p>
                      <p className={`text-base font-bold ${item.highlight ? 'text-blue-700' : 'text-emerald-600'}`}>{item.commission}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-700 mt-2 italic">* Conseil Stratégique et Radar Foncier exclus. Commission de 15% sur le montant HT.</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Affiliate Link Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Link2 className="w-5 h-5 text-blue-500" />
                      Mon Lien d'Affiliation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {getAffiliateLink() ? (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Partagez ce lien — vous recevez <strong>{((member.commission_rate || 0) < 1 ? (member.commission_rate || 0.15) * 100 : (member.commission_rate || 15)).toFixed(0)}% de commission</strong> sur chaque audit payé par vos contacts.
                        </p>
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-700 font-semibold mb-1 uppercase tracking-wide">Votre code :</p>
                          <p className="font-mono text-lg font-bold text-amber-800">{member.affiliate_code}</p>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1 p-3 bg-gray-100 rounded-lg font-mono text-xs break-all">
                            {getAffiliateLink()}
                          </div>
                          <Button variant="outline" onClick={copyAffiliateLink} className="shrink-0" data-testid="copy-affiliate-link">
                            {copiedLink ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-blue-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-blue-600">{((member.commission_rate || 0) < 1 ? (member.commission_rate || 0.15) * 100 : (member.commission_rate || 15)).toFixed(0)}%</p>
                            <p className="text-sm text-gray-600">Commission</p>
                          </div>
                          <div className="p-4 bg-emerald-50 rounded-lg text-center">
                            <p className="text-2xl font-bold text-emerald-600">{affiliateStats?.total_referrals || 0}</p>
                            <p className="text-sm text-gray-600">Parrainages</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                          <h4 className="font-semibold text-blue-800 mb-3 flex items-center gap-2"><Zap className="w-4 h-4" /> Comment ça marche ?</h4>
                          <ul className="text-sm text-blue-700 space-y-2">
                            <li className="flex items-start gap-2"><span className="bg-blue-200 text-blue-800 text-xs font-bold px-2 py-0.5 rounded-full mt-0.5 shrink-0">1</span>Vous activez votre lien personnel (gratuit, 1 clic)</li>
                            <li className="flex items-start gap-2"><span className="bg-blue-200 text-blue-800 text-xs font-bold px-2 py-0.5 rounded-full mt-0.5 shrink-0">2</span>Vous le partagez par SMS, email ou WhatsApp</li>
                            <li className="flex items-start gap-2"><span className="bg-blue-200 text-blue-800 text-xs font-bold px-2 py-0.5 rounded-full mt-0.5 shrink-0">3</span>Votre contact passe sa commande via votre lien</li>
                            <li className="flex items-start gap-2"><span className="bg-emerald-200 text-emerald-800 text-xs font-bold px-2 py-0.5 rounded-full mt-0.5 shrink-0">€</span>Vous recevez <strong>15% de commission</strong> automatiquement</li>
                          </ul>
                          <div className="mt-3 grid grid-cols-3 gap-2">
                            {[
                              { label: 'Audit Flash', gain: '14,55€' },
                              { label: 'Audit Pro', gain: '37,05€' },
                              { label: 'Master VIP', gain: '74,55€' },
                            ].map((item) => (
                              <div key={item.label} className="bg-white rounded-lg p-2 text-center border border-blue-100">
                                <p className="text-xs text-gray-500">{item.label}</p>
                                <p className="text-sm font-bold text-emerald-600">{item.gain}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        <Button onClick={activateAffiliation} disabled={activatingAffiliation} className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-5" data-testid="activate-affiliation-btn">
                          {activatingAffiliation ? 'Activation en cours...' : '✦ Activer mon lien d\'affiliation gratuitement'}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Commission Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-500" />
                      Mes Commissions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {affiliateStats ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 border rounded-lg">
                            <p className="text-sm text-gray-600">Total gagné</p>
                            <p className="text-2xl font-bold text-emerald-600">
                              {formatPrice(affiliateStats.total_commission || 0)}
                            </p>
                          </div>
                          <div className="p-4 border rounded-lg">
                            <p className="text-sm text-gray-600">En attente</p>
                            <p className="text-2xl font-bold text-amber-600">
                              {formatPrice(affiliateStats.pending_commission || 0)}
                            </p>
                          </div>
                        </div>
                        <div className="border-t pt-4">
                          <h4 className="font-medium mb-3">Historique récent</h4>
                          {affiliateStats.recent_commissions?.length > 0 ? (
                            <div className="space-y-2">
                              {affiliateStats.recent_commissions.map((c, idx) => (
                                <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                                  <span>{formatDate(c.date)}</span>
                                  <span className="font-medium text-emerald-600">+{formatPrice(c.amount)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 text-center py-4">Aucune commission pour le moment</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Euro className="w-12 h-12 mx-auto mb-4 opacity-30" />
                        <p>Les statistiques de commission apparaîtront ici.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* === OPPORTUNITÉ 2 : RECRUTER DES AGENCES MARQUE BLANCHE === */}
              <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-600 text-white rounded-xl p-3 shrink-0">
                      <Building2 className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-blue-900 mb-1">Recruter une agence = commission récurrente</h3>
                      <p className="text-sm text-blue-800 mb-3">
                        Proposez notre solution <strong>Marque Blanche</strong> à une agence immobilière : elle obtient sa propre application à son nom. Vous touchez <strong className="text-blue-700">75€ dès la 1ère année</strong> et <strong className="text-blue-700">300€/an</strong> sur les renouvellements.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                        <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                          <p className="text-lg font-bold text-blue-700">75€</p>
                          <p className="text-xs text-gray-600 mt-0.5">1ère souscription (500€)</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                          <p className="text-lg font-bold text-emerald-600">300€</p>
                          <p className="text-xs text-gray-600 mt-0.5">Chaque renouvellement (2 000€/an)</p>
                        </div>
                        <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                          <p className="text-lg font-bold text-amber-600">Récurrent</p>
                          <p className="text-xs text-gray-600 mt-0.5">Tant que l'agence est active</p>
                        </div>
                      </div>
                      <div className="p-3 bg-white border border-blue-200 rounded-lg mb-3">
                        <p className="text-xs text-blue-700 font-semibold mb-1">Message clé à leur dire :</p>
                        <p className="text-sm text-gray-700 italic">
                          "Votre agence aura sa propre application avec son nom. Les vendeurs qui font une estimation gratuite deviennent vos prospects directs — vous n'avez qu'à les rappeler."
                        </p>
                      </div>
                      {getAffiliateLink() && (
                        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg mb-3">
                          <p className="text-xs text-indigo-700 font-semibold mb-1">Lien de recrutement Partenaire :</p>
                          <div className="flex gap-2 items-center">
                            <span className="font-mono text-xs text-indigo-800 break-all flex-1">{`${window.location.origin}/reseau/partenaire/inscription?ref=${member?.affiliate_code}`}</span>
                            <Button variant="outline" size="sm" className="shrink-0" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/reseau/partenaire/inscription?ref=${member?.affiliate_code}`); toast.success('Lien copié !'); }}>
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-gray-500">Pour nous recommander une agence sans lien, contactez-nous directement — nous gérons l'onboarding pour vous.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profil">
            <div className="space-y-5">

              {/* Infos personnelles & société */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-gray-600" />
                      Mon Profil
                    </CardTitle>
                    {!editingProfile ? (
                      <Button size="sm" variant="outline" onClick={() => setEditingProfile(true)} data-testid="edit-profile-btn">
                        <Edit2 className="w-4 h-4 mr-2" /> Modifier
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setEditingProfile(false)}><X className="w-4 h-4 mr-1" /> Annuler</Button>
                        <Button size="sm" onClick={saveProfile} disabled={savingProfile} className="bg-[#0f1d3c] hover:bg-[#162a4f]" data-testid="save-profile-btn">
                          {savingProfile ? 'Enregistrement...' : <><Save className="w-4 h-4 mr-1" /> Enregistrer</>}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Email — toujours non modifiable */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center gap-3">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Email (non modifiable)</p>
                      <p className="text-sm font-medium">{member.email}</p>
                    </div>
                  </div>

                  {!editingProfile ? (
                    /* Mode lecture */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { label: 'Prénom', value: member.prenom },
                        { label: 'Nom', value: member.nom },
                        { label: 'Téléphone', value: member.telephone || member.phone },
                        { label: 'Société', value: member.company_name || member.company_info?.name },
                        { label: 'SIRET', value: member.siret || member.company_info?.siret },
                        { label: 'Adresse', value: member.address || member.company_info?.address },
                        { label: 'Ville', value: member.city || member.company_info?.city },
                        { label: 'Code postal', value: member.postal_code || member.company_info?.postal_code },
                      ].filter(f => f.value).map(({ label, value }) => (
                        <div key={label} className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500">{label}</p>
                          <p className="font-medium text-sm">{value}</p>
                        </div>
                      ))}
                      {/* Métiers artisan en lecture */}
                      {(member.metiers?.length > 0 || member.artisan_info?.trades?.length > 0) && (
                        <div className="md:col-span-2 p-3 bg-emerald-50 rounded-lg">
                          <p className="text-xs text-gray-500 mb-2">Métiers</p>
                          <div className="flex flex-wrap gap-2">
                            {(member.metiers || member.artisan_info?.trades || []).map((t, i) => (
                              <Badge key={i} className="bg-emerald-100 text-emerald-700">{t}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Mode édition */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { key: 'prenom', label: 'Prénom' },
                        { key: 'nom', label: 'Nom *' },
                        { key: 'telephone', label: 'Téléphone' },
                        { key: 'company_name', label: 'Société' },
                        { key: 'siret', label: 'SIRET' },
                        { key: 'address', label: 'Adresse' },
                        { key: 'city', label: 'Ville' },
                        { key: 'postal_code', label: 'Code postal' },
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">{label}</label>
                          <Input
                            value={profileForm[key] || ''}
                            onChange={e => setProfileForm(f => ({ ...f, [key]: e.target.value }))}
                            placeholder={label.replace(' *', '')}
                            data-testid={`profile-${key}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Métiers artisan en lecture quand on édite — non modifiables ici */}
                  {editingProfile && (member.metiers?.length > 0 || member.artisan_info?.trades?.length > 0) && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 mb-2">Métiers (non modifiables ici — contacter l'admin)</p>
                      <div className="flex flex-wrap gap-2">
                        {(member.metiers || member.artisan_info?.trades || []).map((t, i) => (
                          <Badge key={i} variant="outline">{t}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Changer mot de passe */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Shield className="w-4 h-4 text-orange-500" /> Changer le mot de passe
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { key: 'current_password', label: 'Mot de passe actuel', show: showPasswords.current, toggle: () => setShowPasswords(s => ({ ...s, current: !s.current })), testid: 'pw-current' },
                    { key: 'new_password', label: 'Nouveau mot de passe', show: showPasswords.new, toggle: () => setShowPasswords(s => ({ ...s, new: !s.new })), testid: 'pw-new' },
                    { key: 'confirm_password', label: 'Confirmer le mot de passe', show: showPasswords.confirm, toggle: () => setShowPasswords(s => ({ ...s, confirm: !s.confirm })), testid: 'pw-confirm' },
                  ].map(({ key, label, show, toggle, testid }) => (
                    <div key={key}>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">{label}</label>
                      <div className="relative">
                        <Input type={show ? 'text' : 'password'} value={passwordForm[key]} onChange={e => setPasswordForm(f => ({ ...f, [key]: e.target.value }))} placeholder={label} className="pr-10" data-testid={testid} />
                        <button type="button" onClick={toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {key === 'new_password' && passwordForm.new_password && passwordForm.new_password.length < 6 && (
                        <p className="text-xs text-red-500 mt-1">Minimum 6 caractères</p>
                      )}
                      {key === 'confirm_password' && passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
                        <p className="text-xs text-red-500 mt-1">Ne correspond pas</p>
                      )}
                    </div>
                  ))}
                  <Button onClick={savePassword} disabled={savingPassword || !passwordForm.current_password || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password}
                    className="bg-orange-600 hover:bg-orange-700 text-white mt-2" data-testid="save-password-btn">
                    {savingPassword ? 'Modification...' : <><Shield className="w-4 h-4 mr-2" /> Changer le mot de passe</>}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Carte Contact & Support — visible depuis tous les onglets */}
        <div className="mt-6 rounded-xl border border-blue-100 bg-blue-50 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-blue-900 text-sm">Une question ? Contactez l'équipe</p>
            <p className="text-blue-700 text-xs mt-0.5">Modification de vos métiers, problème technique, demande spécifique — on vous répond rapidement.</p>
          </div>
          <a
            href="mailto:auditmarchantdebien@gmail.com"
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
          >
            <Mail className="w-4 h-4" />
            auditmarchantdebien@gmail.com
          </a>
        </div>
      </main>
    </div>
  );
}
